/**
 * DOM manipulation helper functions
 */

/**
 * Decode HTML entities in a string
 */
function decodeHTMLEntities(text: string): string {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
}

/**
 * Setup collapsible behavior for a single article element
 */
export function setupSingleArticle(articleElement: Element): void {
  // Skip if already set up
  if (articleElement.getAttribute('data-collapse-setup')) {
    return;
  }

  articleElement.setAttribute('data-collapse-setup', 'true');

  const h2 = articleElement.querySelector(':scope > h2');
  if (h2) {
    // Check if content is already wrapped - only check direct children
    const existingWrapper = articleElement.querySelector(':scope > .article-content');
    if (!existingWrapper) {
      // Wrap content after h2 - only get direct children
      const content = Array.from(articleElement.children).filter(child =>
        child !== h2 && !child.classList.contains('article-content')
      );

      if (content.length > 0) {
        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'article-content';

        content.forEach(element => {
          contentWrapper.appendChild(element);
        });

        articleElement.appendChild(contentWrapper);
      }
    }

    // Add chevron toggle button if not already added
    if (!h2.querySelector('.article-toggle')) {
      const toggleBtn = document.createElement('span');
      toggleBtn.className = 'article-toggle';
      toggleBtn.innerHTML = `
        <svg class="chevron-up" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.64645 13.3536C4.45118 13.1583 4.45118 12.8417 4.64645 12.6464L9.64645 7.64645C9.84171 7.45118 10.1583 7.45118 10.3536 7.64645L15.3536 12.6464C15.5488 12.8417 15.5488 13.1583 15.3536 13.3536C15.1583 13.5488 14.8417 13.5488 14.6464 13.3536L10 8.70711L5.35355 13.3536C5.15829 13.5488 4.84171 13.5488 4.64645 13.3536Z" fill="currentColor"/>
        </svg>
        <svg class="chevron-down" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.3536 7.64645C15.5488 7.84171 15.5488 8.15829 15.3536 8.35355L10.3536 13.3536C10.1583 13.5488 9.84171 13.5488 9.64645 13.3536L4.64645 8.35355C4.45118 8.15829 4.45118 7.84171 4.64645 7.64645C4.84171 7.45118 5.15829 7.45118 5.35355 7.64645L10 12.2929L14.6464 7.64645C14.8417 7.45118 15.1583 7.45118 15.3536 7.64645Z" fill="currentColor"/>
        </svg>
      `;
      h2.appendChild(toggleBtn);
    }

    // Only add click listener if not already added
    if (!h2.hasAttribute('data-click-handler')) {
      h2.setAttribute('data-click-handler', 'true');
      h2.addEventListener('click', function () {
        articleElement.classList.toggle('collapsed');
      }, { passive: true });
    }
  }
}

/**
 * Setup collapsible behavior for an element panel
 */
export function setupElementPanel(elementPanel: Element): void {
  // Skip if already set up
  if (elementPanel.getAttribute('data-element-setup')) {
    return;
  }

  elementPanel.setAttribute('data-element-setup', 'true');

  const h2 = elementPanel.querySelector(':scope > h2');
  if (h2) {
    // Add chevron toggle button if not already added
    if (!h2.querySelector('.element-toggle')) {
      const toggleBtn = document.createElement('span');
      toggleBtn.className = 'element-toggle';
      toggleBtn.innerHTML = `
        <svg class="chevron-up" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.64645 13.3536C4.45118 13.1583 4.45118 12.8417 4.64645 12.6464L9.64645 7.64645C9.84171 7.45118 10.1583 7.45118 10.3536 7.64645L15.3536 12.6464C15.5488 12.8417 15.5488 13.1583 15.3536 13.3536C15.1583 13.5488 14.8417 13.5488 14.6464 13.3536L10 8.70711L5.35355 13.3536C5.15829 13.5488 4.84171 13.5488 4.64645 13.3536Z" fill="currentColor"/>
        </svg>
        <svg class="chevron-down" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.3536 7.64645C15.5488 7.84171 15.5488 8.15829 15.3536 8.35355L10.3536 13.3536C10.1583 13.5488 9.84171 13.5488 9.64645 13.3536L4.64645 8.35355C4.45118 8.15829 4.45118 7.84171 4.64645 7.64645C4.84171 7.45118 5.15829 7.45118 5.35355 7.64645L10 12.2929L14.6464 7.64645C14.8417 7.45118 15.1583 7.45118 15.3536 7.64645Z" fill="currentColor"/>
        </svg>
      `;
      h2.appendChild(toggleBtn);
    }

    // Only add click listener if not already added
    if (!h2.hasAttribute('data-click-handler')) {
      h2.setAttribute('data-click-handler', 'true');
      h2.addEventListener('click', function () {
        elementPanel.classList.toggle('collapsed');
      }, { passive: true });
    }
  }
}

/**
 * Update streaming content incrementally in a content container
 * Handles both single-element and multi-element formats
 */
export function updateStreamingContent(
  contentRef: HTMLDivElement | null,
  newChunk: string,
  streamingContentRef: { current: string },
  elementContentsRef?: { current: { [elementId: string]: string } }
): void {
  if (!contentRef) return;

  // Check if this chunk contains element tags (multi-element format)
  if (newChunk.includes("<element id='") && elementContentsRef) {
    // Split by element tags to handle multiple chunks in one stream
    const elementTagRegex = /<element id='([^']+)' name='([^']+)'> - /g;
    const parts: Array<{ elementId: string; elementName: string; content: string }> = [];
    
    let match;
    
    while ((match = elementTagRegex.exec(newChunk)) !== null) {
      const elementId = match[1];
      const elementName = match[2];
      const startIndex = match.index + match[0].length;
      
      // Find the next element tag or end of string
      elementTagRegex.lastIndex = startIndex;
      const nextMatch = elementTagRegex.exec(newChunk);
      elementTagRegex.lastIndex = match.index + match[0].length; // Reset for next iteration
      
      const endIndex = nextMatch ? nextMatch.index : newChunk.length;
      const content = newChunk.substring(startIndex, endIndex);
      
      parts.push({ elementId, elementName, content });
    }
    
    // Process each element chunk
    parts.forEach(({ elementId, elementName, content: elementChunk }) => {
      // Skip if chunk is empty or only whitespace
      if (!elementChunk || elementChunk.trim().length === 0) {
        return;
      }
      
      // Initialize element content if not exists
      if (!elementContentsRef.current[elementId]) {
        elementContentsRef.current[elementId] = '';
      }
      
      // Append chunk to element content
      elementContentsRef.current[elementId] += elementChunk;
      
      // Create element panel if it doesn't exist (only when we have content)
      let elementPanel = contentRef.querySelector(`[data-element-id="${elementId}"]`) as HTMLElement;
      if (!elementPanel) {
        elementPanel = document.createElement('div');
        elementPanel.className = 'element-panel';
        elementPanel.setAttribute('data-element-id', elementId);
        elementPanel.setAttribute('data-element-name', elementName);
        
        const header = document.createElement('h2');
        header.textContent = elementName;
        elementPanel.appendChild(header);
        
        const content = document.createElement('div');
        content.className = 'element-content';
        elementPanel.appendChild(content);
        
        // Insert element panel in alphabetical order by element name
        const existingPanels = Array.from(contentRef.querySelectorAll('.element-panel')) as HTMLElement[];
        const insertIndex = existingPanels.findIndex(panel => {
          const panelName = panel.getAttribute('data-element-name') || '';
          return panelName.localeCompare(elementName) > 0;
        });
        
        if (insertIndex === -1) {
          // Append at the end if no panel comes after alphabetically
          contentRef.appendChild(elementPanel);
        } else {
          // Insert before the panel that comes after alphabetically
          contentRef.insertBefore(elementPanel, existingPanels[insertIndex]);
        }
        
        setupElementPanel(elementPanel);
      }
      
      // Update the specific element's content
      if (elementPanel) {
        const elementContentDiv = elementPanel.querySelector('.element-content') as HTMLElement;
        if (elementContentDiv) {
          updateElementContent(elementContentDiv, elementContentsRef.current[elementId]);
          
          // Hide panel if content is empty
          if (elementContentDiv.children.length === 0 && elementContentDiv.textContent?.trim() === '') {
            elementPanel.style.display = 'none';
          } else {
            elementPanel.style.display = '';
          }
        }
      }
    });
  } else {
    // Single-element format (legacy)
    streamingContentRef.current += newChunk;
    updateElementContent(contentRef, streamingContentRef.current);
  }
}

/**
 * Update content in a specific element container
 */
function updateElementContent(container: HTMLElement, content: string): void {
  try {
    // Decode any HTML entities in the content
    const decodedContent = decodeHTMLEntities(content);
    
    // Create a temporary div to parse the new HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = decodedContent;

    // Get all children from the temp div
    const children = Array.from(tempDiv.children);
    const currentChildren = Array.from(container.children);

    // Only add new children that don't already exist
    children.forEach((child, index) => {
      if (index >= currentChildren.length) {
        const clonedChild = child.cloneNode(true) as Element;

        // If it's an article, set it up for collapsing immediately
        if (clonedChild.classList.contains('article')) {
          setupSingleArticle(clonedChild);
        }

        container.appendChild(clonedChild);
      } else {
        // Update existing child if content has changed (streaming more content)
        const existingChild = currentChildren[index];
        const newInnerHTML = child.innerHTML;

        // Only update if there's a meaningful difference (content has changed)
        if (existingChild.innerHTML !== newInnerHTML) {
          // Preserve collapsed state if it's an article
          const wasCollapsed = existingChild.classList.contains('collapsed');

          existingChild.innerHTML = newInnerHTML;

          // Re-setup if it's an article and content changed
          if (existingChild.classList.contains('article')) {
            // Remove old setup
            existingChild.removeAttribute('data-collapse-setup');
            const oldWrapper = existingChild.querySelector('.article-content');
            if (oldWrapper) {
              // Move children out of wrapper
              Array.from(oldWrapper.children).forEach(child => {
                existingChild.appendChild(child);
              });
              oldWrapper.remove();
            }
            setupSingleArticle(existingChild);

            // Restore collapsed state
            if (wasCollapsed) {
              existingChild.classList.add('collapsed');
            }
          }
        }
      }
    });
  } catch (error) {
    // Fallback to simple innerHTML if parsing fails
    console.warn('Failed to parse streaming content incrementally:', error);
    const decodedContent = decodeHTMLEntities(content);
    container.innerHTML = decodedContent;
    // Setup any articles that may have been added
    const articles = container.querySelectorAll('.article:not([data-collapse-setup])');
    articles.forEach(article => setupSingleArticle(article));
  }
}

/**
 * Setup collapsible articles in a container after content is rendered
 */
export function setupArticles(container: HTMLElement): void {
  // Only select direct child articles that haven't been set up yet
  const articles = container.querySelectorAll(':scope > .article:not([data-collapse-setup])');
  articles.forEach(article => setupSingleArticle(article));
}
